import { AddEdit as AddEditInterface } from '../../../../interfaces/transaction.interface';
import { Validation } from '../../../../interfaces/common';
import { APP_ERROR_MESSAGES, VALIDATION_FIELD_MAPPING, MaxLength, Regex, ATTACHMENT } from '../../../../utils/appConstant';

export interface AttachmentValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Per-file validation for the attachment upload queue — checked before any
 * network call (AC3). Mirrors, but does not replace, the server-side
 * validation in ChunkedUploadService::initSession (AC9).
 */
export const validateAttachmentFile = (file: File): AttachmentValidationResult => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (!ATTACHMENT.allowedExtensions.includes(extension)) {
    return { valid: false, error: APP_ERROR_MESSAGES.transactions.addEdit.invalidAttachmentType };
  }

  if (file.size > ATTACHMENT.maxSizeBytes) {
    return { valid: false, error: APP_ERROR_MESSAGES.transactions.addEdit.attachmentTooLarge };
  }

  return { valid: true };
};

export const validateAddEditForm = (formData: AddEditInterface, fieldToValidate?: string): Validation => {
  const validations: Validation = { validate: true, fields: [] };

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.expenseDetails) {
    if (!formData.expenseDetails) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.expenseDetails,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankExpenseDetails,
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.transactionDate) {
    if (!formData.transactionDate) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.transactionDate,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankTransactionDate,
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.propertyId) {
    if (!formData.propertyId) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.propertyId,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankProperty,
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.transactionType) {
    if (!formData.transactionType) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.transactionType,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankTransactionType,
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo) {
    if (formData.carryforwardInvoiceNo?.trim() && formData.carryforwardInvoiceNo.trim().length > MaxLength.common) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo,
        message: APP_ERROR_MESSAGES.transactions.addEdit.maxLengthExceeded,
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.actualCost) {
    if (formData.actualCost && !Regex.floatValues.test(formData.actualCost.trim())) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.actualCost,
        message: APP_ERROR_MESSAGES.transactions.addEdit.invalidActualCost,
      });
    } else {
      const numValue = parseFloat(formData.actualCost);
      if (numValue < 0) {
        validations.validate = false;
        validations.fields.push({
          field: VALIDATION_FIELD_MAPPING.actualCost,
          message: APP_ERROR_MESSAGES.transactions.addEdit.invalidActualCost,
        });
      }
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.totalAmount) {
    if (!formData.totalAmount) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.totalAmount,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankTotalAmount,
      });
    } else if (!Regex.floatValues.test(formData.totalAmount.trim())) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.totalAmount,
        message: APP_ERROR_MESSAGES.transactions.addEdit.invalidTotalAmount,
      });
    } else {
      const numValue = parseFloat(formData.totalAmount);
      if (numValue < 0) {
        validations.validate = false;
        validations.fields.push({
          field: VALIDATION_FIELD_MAPPING.totalAmount,
          message: APP_ERROR_MESSAGES.transactions.addEdit.invalidTotalAmount,
        });
      }
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.subTotal) {
    if (!formData.subTotal) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.subTotal,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankSubTotal,
      });
    } else if (!Regex.floatValues.test(formData.subTotal.trim())) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.subTotal,
        message: APP_ERROR_MESSAGES.transactions.addEdit.invalidSubTotal,
      });
    } else {
      const numValue = parseFloat(formData.subTotal);
      if (numValue < 0) {
        validations.validate = false;
        validations.fields.push({
          field: VALIDATION_FIELD_MAPPING.subTotal,
          message: APP_ERROR_MESSAGES.transactions.addEdit.invalidSubTotal,
        });
      }
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.reviewed || fieldToValidate === VALIDATION_FIELD_MAPPING.reviewedBy) {
    const reviewed = !formData.reviewed;
    if (reviewed && !formData.reviewedBy) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.reviewedBy,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankReviewedBy
      });
    }
  }

  if (!fieldToValidate || fieldToValidate === VALIDATION_FIELD_MAPPING.reviewed || fieldToValidate === VALIDATION_FIELD_MAPPING.notes) {
    if (!formData.reviewed && !formData.notes?.trim()) {
      validations.validate = false;
      validations.fields.push({
        field: VALIDATION_FIELD_MAPPING.notes,
        message: APP_ERROR_MESSAGES.transactions.addEdit.blankNotes
      });
    }
  }

  return validations;
};
